import { Button, AsyncButton } from 'system/atoms';
import { AddToJobActionButton } from 'modules/candidate-profile/components/AddToJobActionButton';
import { WorkflowBuilderIcon } from 'common/icons/WorkflowBuilderIcon';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';

export type GetCandidateProfileSelectionActionBarItemsProps = {
  candidateProfileListProps: CandidateProfileListProps;
  onInviteToApply: () => void;
  onAddToTalentList: () => Promise<void>;
  onAddToWorkflow: () => void;
  onRemoveFromTalentList: () => void;
  onAddToJob: () => void;
  onAddToJobAction: () => void;
  isAtsConnected: boolean;
  /**
   * The org has configured a system of its own to hand the selection to. It stands in for
   * the ATS drawer only where there is no ATS: a connected ATS keeps its own action rather
   * than having a second button of the same name beside it.
   */
  hasAddToJobAction?: boolean;
  isAddToJobActionOverLimit?: boolean;
  /**
   * The recruiter arrived from the workflow builder to populate one workflow. That is the whole
   * errand, so the other actions would be detours away from it.
   */
  isFromWorkflowBuilder?: boolean;
  /**
   * The org has the workflow builder. Without it there are no workflows to push into, and both
   * links out of the drawer would lead into a product the org cannot reach — so the action is
   * withheld entirely rather than offered and then failing.
   */
  isWorkflowBuilderEnabled?: boolean;
  isInviteToApplyHidden: boolean;
};

/**
 * Which of the two add-to-job routes a list offers. There is only ever one button under
 * that name: a connected ATS keeps its own drawer, and the organisation's configured URL
 * stands in only where there is no ATS to keep.
 */
type AddToJobRoute = 'ats' | 'configuredUrl' | 'none';

const getAddToJobRoute = (
  props: GetCandidateProfileSelectionActionBarItemsProps
): AddToJobRoute => {
  if (props.isAtsConnected) {
    return 'ats';
  }

  if (props.hasAddToJobAction) {
    return 'configuredUrl';
  }

  return 'none';
};

export const getCandidateProfileSelectionActionBarItems = (
  props: GetCandidateProfileSelectionActionBarItemsProps
) => {
  const addToJobRoute = getAddToJobRoute(props);

  switch (props.candidateProfileListProps.type) {
    case CandidateProfileListType.CANDIDATES_LIST:
      if (props.isFromWorkflowBuilder && props.isWorkflowBuilderEnabled) {
        return (
          <Button
            variant="primary"
            endIcon={<WorkflowBuilderIcon size={20} color="currentColor" />}
            onClick={props.onAddToWorkflow}
          >
            Add to workflow
          </Button>
        );
      }

      return (
        <>
          {addToJobRoute === 'configuredUrl' && (
            <AddToJobActionButton
              isOverCandidateLimit={Boolean(props.isAddToJobActionOverLimit)}
              onClick={props.onAddToJobAction}
            />
          )}
          {addToJobRoute === 'ats' && (
            <Button variant="primary" onClick={props.onAddToJob}>
              Add to job
            </Button>
          )}
          {props.isWorkflowBuilderEnabled && (
            <Button
              variant="secondary"
              endIcon={<WorkflowBuilderIcon size={20} color="currentColor" />}
              onClick={props.onAddToWorkflow}
            >
              Add to workflow
            </Button>
          )}
          {!props.isInviteToApplyHidden && (
            <Button variant="secondary" onClick={props.onInviteToApply}>
              Invite to apply
            </Button>
          )}
          <Button variant="secondary" onClick={props.onAddToTalentList}>
            Add to talent list
          </Button>
        </>
      );
    case CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL:
      return (
        <>
          {!props.isInviteToApplyHidden && (
            <Button variant="primary" onClick={props.onInviteToApply}>
              Invite to apply
            </Button>
          )}
          <Button variant="secondary" onClick={props.onRemoveFromTalentList}>
            Remove from list
          </Button>
        </>
      );
    case CandidateProfileListType.ADD_CANDIDATES_TO_TALENT_POOL:
      return (
        <>
          <AsyncButton
            variant="primary"
            loadingType="bar"
            onClick={props.onAddToTalentList}
          >
            Add to talent list
          </AsyncButton>
        </>
      );
    case CandidateProfileListType.ANALYSIS_CANDIDATES_LIST:
    case CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL:
      return (
        <>
          {addToJobRoute === 'configuredUrl' && (
            <AddToJobActionButton
              isOverCandidateLimit={Boolean(props.isAddToJobActionOverLimit)}
              onClick={props.onAddToJobAction}
            />
          )}
          {addToJobRoute === 'ats' && (
            <Button variant="primary" onClick={props.onAddToJob}>
              Add to job
            </Button>
          )}
          {/* Invite to apply leads only when no add-to-job button took the lead. */}
          {!props.isInviteToApplyHidden && (
            <Button
              variant={addToJobRoute === 'none' ? 'primary' : 'secondary'}
              onClick={props.onInviteToApply}
            >
              Invite to apply
            </Button>
          )}
        </>
      );
  }
};
